/**
 * date-picker.tsx — Selector de fecha controlado (Popover + Calendar).
 * El valor de entrada/salida es SIEMPRE un string ISO `yyyy-MM-dd` (o "" vacío),
 * para que el almacenamiento del informe no cambie respecto al <input type="date">.
 * Estética FORJA: trigger con aspecto de Input (h-10, borde input), icono calendario.
 */
import * as React from "react";
import { format, parseISO, isValid } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover.js";
import { Calendar } from "@/shared/ui/calendar.js";
import { cn } from "@/shared/lib/utils.js";

/** Parsea un ISO `yyyy-MM-dd` a Date local válido; null si vacío o inválido. */
function parseIso(value: string): Date | undefined {
  if (!value) return undefined;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : undefined;
}

export interface DatePickerProps {
  /** Fecha en ISO `yyyy-MM-dd`, o "" para vacío. */
  value: string;
  /** Llama con el nuevo ISO `yyyy-MM-dd` al seleccionar un día. */
  onChange: (iso: string) => void;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  /** Additional Tailwind classes applied to the PopoverTrigger button.
   *  Uses tailwind-merge so these safely override the default trigger styles. */
  triggerClassName?: string;
}

export function DatePicker({
  value,
  onChange,
  placeholder = "Seleccionar fecha",
  id,
  disabled,
  triggerClassName,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const selected = parseIso(value);

  function handleSelect(day: Date | undefined) {
    if (day && isValid(day)) {
      onChange(format(day, "yyyy-MM-dd"));
      setOpen(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        id={id}
        type="button"
        disabled={disabled}
        className={cn(
          "flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-left text-sm",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          triggerClassName,
        )}
      >
        <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className={cn("flex-1 truncate", !selected && "text-muted-foreground")}>
          {selected ? format(selected, "dd/MM/yyyy", { locale: es }) : placeholder}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      </PopoverTrigger>
      <PopoverContent align="start" className="p-3">
        {/* ponytail: autoFocus removed — in Chrome it triggers a blur on the trigger
            inside the Radix Portal, causing PopoverContent to fire onInteractOutside
            before onSelect fires, so the selected date is never applied. */}
        <Calendar
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          defaultMonth={selected}
        />
      </PopoverContent>
    </Popover>
  );
}
